import { F0Alert, F0Box, F0Card, F0Text } from '@factorialco/f0-react'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import { TrainingDetailFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import DropzoneBox from 'modules/documents/components/Documents/DropzoneBox'
import { useScormPackageStatus } from 'modules/trainings/hooks/ScormPackage/useScormPackageStatus'
import { useScormPackageUpload } from 'modules/trainings/hooks/ScormPackage/useScormPackageUpload'

export type ScormPackage = NonNullable<TrainingDetailFragment['scormPackage']>

interface Props {
  trainingId: string
  companyId: string
  scormPackage: ScormPackage | null | undefined
  onUploadSuccess?: () => void
}

const IMPORT_STATUS_PENDING = 'pending'
const IMPORT_STATUS_RUNNING = 'running'
const IMPORT_STATUS_COMPLETE = 'complete'
const IMPORT_STATUS_ERROR = 'error'

const isProcessing = (status: string) =>
  [IMPORT_STATUS_PENDING, IMPORT_STATUS_RUNNING].includes(status.toLowerCase())

const isComplete = (status: string) => status.toLowerCase() === IMPORT_STATUS_COMPLETE

const isError = (status: string) => status.toLowerCase() === IMPORT_STATUS_ERROR

const SpinnerCard = ({ label }: { label: string }) => (
  <F0Card compact>
    <F0Box display="flex" flexDirection="row" gap="sm" alignItems="center" justifyContent="center">
      <Spinner />
      <F0Text content={label} variant="body" />
    </F0Box>
  </F0Card>
)

const ScormPackageUpload = ({ trainingId, companyId, scormPackage, onUploadSuccess }: Props) => {
  const {
    upload,
    uploading,
    uploadState,
    error: uploadError,
    reset,
  } = useScormPackageUpload({
    trainingId,
    companyId,
    onSuccess: onUploadSuccess,
  })

  useScormPackageStatus({
    scormPackageId: scormPackage?.id,
    importStatus: scormPackage?.importStatus,
    skip: !scormPackage,
  })

  if (uploading) {
    return <SpinnerCard label={i18n.t('trainings.scorm.upload.uploading')} />
  }

  if (scormPackage && isProcessing(scormPackage.importStatus)) {
    return <SpinnerCard label={i18n.t('trainings.scorm.upload.processing')} />
  }

  if (scormPackage && isComplete(scormPackage.importStatus)) {
    return (
      <F0Card
        compact
        title={scormPackage.title ?? i18n.t('trainings.scorm.upload.complete')}
        description={scormPackage.scormVersion ?? undefined}
      />
    )
  }

  if ((scormPackage && isError(scormPackage.importStatus)) || uploadState === 'error') {
    const errorDetail = scormPackage?.importError ?? uploadError
    return (
      <F0Box display="flex" flexDirection="column" gap="md" width="full">
        <F0Alert
          variant="critical"
          title={i18n.t('trainings.scorm.upload.error')}
          description={errorDetail ?? ''}
        />
        <DropzoneBox
          accept={{
            'application/zip': ['.zip'],
            'application/x-zip-compressed': ['.zip'],
          }}
          message={i18n.t('trainings.scorm.upload.retry')}
          multiple={false}
          onClick={reset}
          onDrop={(files) => {
            const file = files[0]
            if (file) void upload(file)
          }}
        />
      </F0Box>
    )
  }

  return (
    <DropzoneBox
      accept={{
        'application/zip': ['.zip'],
        'application/x-zip-compressed': ['.zip'],
      }}
      iconName="file-upload"
      message={i18n.t('trainings.scorm.upload.title')}
      description={i18n.t('trainings.scorm.upload.description')}
      multiple={false}
      disabled={uploading}
      onDrop={(files) => {
        const file = files[0]
        if (file) void upload(file)
      }}
    />
  )
}

export default ScormPackageUpload
